import { useQuery } from '@tanstack/react-query';
import { sprintApi, type Sprint, type SprintEpic, fetchSprintRuns, type SprintRun, type SprintRunsResponse } from '../api/sprint';
import { coderApi, type ExecutionJob, type ActiveSprite } from '../api/coder';

/**
 * Resolve the active sprint for a project.
 * Priority: building > most recent failed > most recent completed
 */
async function resolveActiveSprint(projectId: string): Promise<Sprint | null> {
  // 1. Currently building
  const building = await sprintApi.list(projectId, 'building');
  if (building.sprints.length > 0) return building.sprints[0];

  // 2. Most recent completed or failed (by date)
  const [completed, failed] = await Promise.all([
    sprintApi.list(projectId, 'completed').then(r => r.sprints),
    sprintApi.list(projectId, 'failed').then(r => r.sprints),
  ]);
  const all = [...completed, ...failed].sort(
    (a, b) => new Date(b.completedAt || b.failedAt || b.createdAt).getTime() -
      new Date(a.completedAt || a.failedAt || a.createdAt).getTime()
  );
  if (all.length > 0) return all[0];

  return null;
}

/**
 * Hook to fetch the active sprint for a project.
 */
export function useActiveSprint(projectId: string | undefined) {
  return useQuery({
    queryKey: ['sprint', 'active', projectId],
    queryFn: () => resolveActiveSprint(projectId!),
    enabled: !!projectId,
    refetchInterval: 15_000,
    staleTime: 10_000,
  });
}

/**
 * Hook to fetch epics for a sprint.
 */
export function useSprintEpics(sprintId: string | undefined) {
  return useQuery({
    queryKey: ['sprint', 'epics', sprintId],
    queryFn: () => sprintApi.getEpics(sprintId!),
    enabled: !!sprintId,
    refetchInterval: 15_000,
    staleTime: 10_000,
  });
}

/**
 * Hook to fetch execution status for a batch.
 */
export function useExecution(batchId: string | undefined) {
  return useQuery({
    queryKey: ['coder', 'execution', batchId],
    queryFn: () => coderApi.getExecution(batchId!),
    enabled: !!batchId,
    refetchInterval: 5_000,
    staleTime: 3_000,
  });
}

/**
 * Hook to find the most recent planning sprint for a project (within 7 days).
 * Used by SprintPrepPage to detect resumable sprints.
 */
export function usePlanningSprintForProject(projectId: string | undefined) {
  return useQuery({
    queryKey: ['sprint', 'planning', projectId],
    queryFn: async () => {
      const result = await sprintApi.list(projectId!, 'planning');
      const sevenDaysAgo = Date.now() - 7 * 24 * 60 * 60 * 1000;
      const recent = result.sprints.filter(
        (s) => new Date(s.createdAt).getTime() > sevenDaysAgo
      );
      return recent[0] ?? null;
    },
    enabled: !!projectId,
    staleTime: 5_000,
  });
}

/**
 * Hook to fetch ALL planning sprints for a project (within 7 days).
 * Used by SprintBoardPage for multi-sprint selection.
 * @deprecated Prefer `useSprint()` from SprintContext for sprint selection state.
 */
export function usePlanningSprintsForProject(projectId: string | undefined) {
  return useQuery({
    queryKey: ['sprint', 'planning', projectId],
    queryFn: async () => {
      const result = await sprintApi.list(projectId!, 'planning');
      const sevenDaysAgo = Date.now() - 7 * 24 * 60 * 60 * 1000;
      return result.sprints.filter(
        (s) => new Date(s.createdAt).getTime() > sevenDaysAgo
      );
    },
    enabled: !!projectId,
    staleTime: 5_000,
  });
}

/**
 * Hook to fetch ALL sprints for a project (no status filter).
 * Useful for consumers that don't want the full SprintContext provider.
 */
export function useAllSprints(projectId: string | undefined) {
  return useQuery({
    queryKey: ['sprints', 'all', projectId],
    queryFn: async () => {
      const result = await sprintApi.list(projectId!);
      return result.sprints;
    },
    enabled: !!projectId,
    staleTime: 5_000,
  });
}

/**
 * Hook to fetch all runs for a sprint.
 * Polls every 10s when a run is in 'building' status.
 */
export function useSprintRuns(sprintId: string | undefined) {
  const query = useQuery<SprintRunsResponse>({
    queryKey: ['sprint', 'runs', sprintId],
    queryFn: () => fetchSprintRuns(sprintId!),
    enabled: !!sprintId,
    staleTime: 5_000,
    refetchInterval: (query) => {
      const runs = query.state.data?.runs;
      if (runs?.some(r => r.status === 'building')) return 10_000;
      return 30_000;
    },
  });
  return query;
}

export type { Sprint, SprintEpic, SprintRun, SprintRunsResponse, ExecutionJob, ActiveSprite };
